'use client';

import { useState, useCallback } from 'react';
import { adminKycApi } from '../api';
import { DocumentReviewResponse } from '../types';

interface UseDocumentReviewReturn {
  approve: (documentId: string, tenantSlug: string, adminUserId?: string) => Promise<DocumentReviewResponse>;
  reject: (documentId: string, tenantSlug: string, rejectionReason: string, adminUserId?: string) => Promise<DocumentReviewResponse>;
  isProcessing: boolean;
  error: Error | null;
  reset: () => void;
}

export function useDocumentReview(): UseDocumentReviewReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const approve = useCallback(
    async (documentId: string, tenantSlug: string, adminUserId?: string) => {
      try {
        setIsProcessing(true);
        setError(null);
        const result = await adminKycApi.approveDocument(documentId, tenantSlug, adminUserId);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to approve document'));
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const reject = useCallback(
    async (documentId: string, tenantSlug: string, rejectionReason: string, adminUserId?: string) => {
      try {
        setIsProcessing(true);
        setError(null);
        const result = await adminKycApi.rejectDocument(documentId, tenantSlug, rejectionReason, adminUserId);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to reject document'));
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setIsProcessing(false);
    setError(null);
  }, []);

  return {
    approve,
    reject,
    isProcessing,
    error,
    reset,
  };
}
